import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useApi, configApiRef } from '@backstage/core-plugin-api';
import { Page, Content, Progress } from '@backstage/core-components';
import { EmptyState } from '../shared';
import { useUserIdentity } from '../../hooks';
import { AccessOverview } from './components/AccessOverview';
import { PermissionsTable } from './components/PermissionsTable';
import { Breadcrumbs, ErrorToast, InlineError } from '@internal/backstage-plugin-global-header';

const useStyles = makeStyles(() => ({}));

interface UserPermission {
  product: string;
  permission: string;
  isPending?: boolean;
  pendingPermission?: string;
  currentPermission?: string;
  productOwners?: string[];
}

interface ApiResponse {
  user_permissions: Array<Record<string, string>>;
  pending_approvals: Array<Record<string, string | undefined>>;
}

interface Product {
  id: number;
  product_name: string;
  rbac_enabled: boolean;
}


const RBACPlugin = () => {
  useStyles();
  const { userEmail, userName, fetchUserIdentity } = useUserIdentity();
  const [userPermissions, setUserPermissions] = useState<UserPermission[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasAicoeSuperUser, setHasAicoeSuperUser] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const [reasonDialog, setReasonDialog] = useState({
    open: false,
    product: '',
    permissionType: 'write' as 'write' | 'admin',
    reason: '',
  });

  const showSuccess = (message: string) => setSuccessMessage(message);
  const showError = (message: string) => setActionError(message);

  const configApi = useApi(configApiRef);
  const baseUrl = configApi.getString('backend.baseUrl');

  const permissionStats = useMemo(() => {
    if (hasAicoeSuperUser) {
      const superUserCount = userPermissions.filter(
        perm => perm.permission.toLowerCase() === 'super_admin',
      ).length;

      return [
        { label: 'Super Admin', count: superUserCount, color: '#6366f1' },
      ];
    }

    const stats = userPermissions.reduce((acc, perm) => {
      const level = (perm.currentPermission || 'read').toLowerCase();

      if (level !== 'super_admin') {
        acc[level] = (acc[level] || 0) + 1;
      }

      return acc;
    }, {} as Record<string, number>);

    return [
      { label: 'Admin', count: stats.admin || 0, color: '#ec4899' },
      { label: 'Write', count: stats.write || 0, color: '#06b6d4' },
      { label: 'Read', count: stats.read || 0, color: '#10b981' },
    ];
  }, [userPermissions, hasAicoeSuperUser]);

  const fetchUserRolesAndProducts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const { email, token } = await fetchUserIdentity();

      const url = `${baseUrl}/api/custom-api`;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const [rolesResponse, productsResponse] = await Promise.all([
        fetch(`${url}/rbac?user_mail=${email}`, { method: 'GET', headers }),
        fetch(`${url}/products`, { method: 'GET', headers }),
      ]);

      if (!rolesResponse.ok) throw new Error('Failed to fetch your role assignments');
      if (!productsResponse.ok) throw new Error('Failed to fetch product list');

      const rolesData: ApiResponse[] = await rolesResponse.json();
      const productsData: Product[] = await productsResponse.json();
      const pendingApprovals = rolesData[0]?.pending_approvals || [];

      let userRoles = Array.isArray(rolesData[0]?.user_permissions)
        ? rolesData[0].user_permissions
        : [];

      const userPermissionsMap: Record<string, string> = {};
      const pendingPermissionsMap: Record<
        string,
        { permission: string; productOwners: string[] }
      > = {};
      let hasAicoeMarketplaceSuperUser = false;

      pendingApprovals.forEach(pendingObj => {
        Object.entries(pendingObj).forEach(([key, value]) => {
          if (key === 'product_owner') return;

          const productOwners = pendingObj.product_owner
            ? pendingObj.product_owner
                .split(',')
                .map((owner: string) => owner.trim())
            : [];

          pendingPermissionsMap[key] = {
            permission: value as string,
            productOwners,
          };
        });
      });

      userRoles.forEach(permissionObj => {
        Object.entries(permissionObj).forEach(([product, permission]) => {
          userPermissionsMap[product] = permission;
          if (
            permission.toLowerCase() === 'super_admin' &&
            product.toLowerCase() === 'aicoe_marketplace'
          ) {
            hasAicoeMarketplaceSuperUser = true;
          }
        });
      });

      setHasAicoeSuperUser(hasAicoeMarketplaceSuperUser);

      let filteredProducts: Product[];

      if (hasAicoeMarketplaceSuperUser) {
        filteredProducts = productsData.filter(
          product =>
            product?.product_name?.toLowerCase() === 'aicoe_marketplace',
        );
      } else {
        filteredProducts = productsData.filter(
          product => product?.rbac_enabled === true,
        );
      }

      // Create complete permissions list for filtered products
      const completePermissions: UserPermission[] = filteredProducts
        .map(product => {
          const productName = product?.product_name || 'unknown';
          const pendingInfo = pendingPermissionsMap[productName];
          const isPending = !!pendingInfo;
          const currentPermission = userPermissionsMap[productName];

          if (
            hasAicoeMarketplaceSuperUser &&
            productName.toLowerCase() === 'aicoe_marketplace'
          ) {
            return {
              product: productName,
              permission: 'super_admin',
              currentPermission: currentPermission || 'read',
              isPending: false,
              productOwners: [],
            };
          }

          if (currentPermission && !isPending) {
            return {
              product: productName,
              permission: currentPermission,
              currentPermission,
              isPending: false,
              productOwners: [],
            };
          }

          return {
            product: productName,
            permission: isPending ? pendingInfo.permission : 'read',
            currentPermission: currentPermission || 'read',
            isPending,
            pendingPermission: isPending ? pendingInfo.permission : undefined,
            productOwners: isPending ? pendingInfo.productOwners : [],
          };
        })
        .filter(perm => perm.product && perm.product !== 'unknown');

      setUserPermissions(completePermissions);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'An unknown error occurred',
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUserIdentity, baseUrl]);

  useEffect(() => {
    fetchUserRolesAndProducts();
  }, []);

  const openReasonDialog = (
    product: string,
    permissionType: 'write' | 'admin',
  ) => {
    setReasonDialog({ open: true, product, permissionType, reason: '' });
  };

  const handleRequestPermission = async () => {
    const { product, permissionType, reason } = reasonDialog;
    if (!reason.trim()) {
      showError('Please provide a reason for the access request.');
      return;
    }
    try {
      const { token } = await fetchUserIdentity();
      const url = `${baseUrl}/api/custom-api`;
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      };

      const payload = {
        requested_by: userEmail,
        requested_at: new Date().toISOString(),
        product,
        role: permissionType,
        requested_user_name: userName,
        requested_reason: reason,
      };

      const response = await fetch(`${url}/approval`, {
        method: 'POST',
        headers,
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showError(`Failed to request permission: ${errorData.error}`);
        return;
      }

      showSuccess(
        `${permissionType.charAt(0).toUpperCase() + permissionType.slice(1)} permission request submitted successfully for ${product}!`,
      );

      await fetchUserRolesAndProducts();
    } catch (error) {
      showError('Failed to submit your access request. Please try again.');
    }

    setReasonDialog({
      open: false,
      product: '',
      permissionType: 'write',
      reason: '',
    });
  };

  const hasPendingApprovals = useMemo(() => {
    return userPermissions.some(perm => perm.isPending);
  }, [userPermissions]);

  if (loading) {
    return <Progress />;
  }

  if (error) {
    return (
      <Page themeId="tool">
        <Content>
          <Breadcrumbs />
          <Box mt={2}>
            <InlineError
              title="Your role assignments could not be loaded"
              description="Your existing access and permissions are unaffected."
              onRetry={fetchUserRolesAndProducts}
            />
          </Box>
        </Content>
      </Page>
    );
  }

  return (
    <Page themeId="tool">
      <Content>
        <Breadcrumbs />
        <AccessOverview
          hasAicoeSuperUser={hasAicoeSuperUser}
          permissionStats={permissionStats}
        />

        {userPermissions.length === 0 ? (
          <EmptyState
            title="🔍 No Permissions Found"
            message="No permissions have been assigned to your account. Please contact your administrator for access."
          />
        ) : (
          <PermissionsTable
            permissions={userPermissions}
            hasPendingApprovals={hasPendingApprovals}
            onRequestPermission={openReasonDialog}
          />
        )}
      </Content>

      <Dialog
        open={reasonDialog.open}
        onClose={() =>
          setReasonDialog({
            open: false,
            product: '',
            permissionType: 'write',
            reason: '',
          })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Request {reasonDialog.permissionType === 'admin' ? 'Admin' : 'Write'}{' '}
          Access
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Access Request"
            fullWidth
            multiline
            rows={3}
            value={reasonDialog.reason}
            onChange={e =>
              setReasonDialog(prev => ({ ...prev, reason: e.target.value }))
            }
            placeholder="Please explain why you need this access..."
            variant="outlined"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setReasonDialog({
                open: false,
                product: '',
                permissionType: 'write',
                reason: '',
              })
            }
            color="default"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRequestPermission}
            color="primary"
            variant="contained"
            disabled={!reasonDialog.reason.trim()}
          >
            Submit Request
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        message={successMessage}
        ContentProps={{ style: { backgroundColor: '#4caf50', color: 'white', fontWeight: 500 } }}
      />
      <ErrorToast message={actionError} onClose={() => setActionError(null)} />
    </Page>
  );
};

export default RBACPlugin;
